import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import useMaintenanceWorkflowSchedule from "../../hooks/useWorkflowSchedules";
import useUpdateMaintenanceWorkflowSchedule from "../../hooks/useUpdateWorkflowSchedules";
import { createWorkflowSchedulesSchema, CreateWorkflowSchedulesSchema } from "../../services/workflowSchedules.schema";
import { MaintenanceWorkflowSchedulesTransformer } from "../../services/maintenanceWorkflowSchedulesTransformer";
import AccordionMaintenanceWorkflowSchedulesOverview from "../../components/AccordionOverview";
import { maintenanceWorkflowSchedulesKindOptions } from "../../constants/kind.constant";
import AccordionMaintenanceWorkflowSchedulesEquipments from "../../components/AccordionEquipments";
import AccordionMaintenanceWorkflowSchedulesExecution from "../../components/AccordionExecution";
import AccordionMaintenanceWorkflowSchedulesObjectives from "../../components/AccordionObjectives";

const Update = () => {
  const { id } = useParams();

  const goBack = useGoBack();
  const queryClient = useQueryClient()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToUpdateMaintenanceWorkflowSchedules =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::WorkflowSchedules::Update')

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: workflowSchedule,
    isLoading: workflowScheduleIsLoading
  } = useMaintenanceWorkflowSchedule(String(id))

  const {
    mutate: updateMaintenanceWorkflowSchedule,
    isPending: updateMaintenanceWorkflowScheduleIsPending
  } = useUpdateMaintenanceWorkflowSchedule({
    id,
    workflowSchedule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['maintenanceWorkflowSchedule', id]})
      goBack({fallback: `/maintenance-workflows-schedules/${id}`})
    },
    onError: (error: any) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  })

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<CreateWorkflowSchedulesSchema>({
    resolver: zodResolver(createWorkflowSchedulesSchema)
  });

  const onSubmit: SubmitHandler<CreateWorkflowSchedulesSchema> = (data) => updateMaintenanceWorkflowSchedule(data);

  useEffect(() => {
    if (!workflowScheduleIsLoading && workflowSchedule) {
      const formData = MaintenanceWorkflowSchedulesTransformer.formDataTransformer(workflowSchedule)
      reset(formData)
    }
  }, [workflowScheduleIsLoading])

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: '/maintenance-workflows-schedules' }}
        title="Programação de Manutenção"
        subtitle={workflowSchedule?.display_name}
        buttons={
          <div className="flex items-center gap-4">
            <Button
              variant='outline-error'
              onClick={() => goBack({fallback: `/maintenance-workflows-schedules${id}`})}
            >
              Cancelar
            </Button>
            {userHasPermissionToUpdateMaintenanceWorkflowSchedules && (
              <Button
                type="submit"
                name="submit"
              >
                Salvar
              </Button>
            )}
          </div>
        }
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToUpdateMaintenanceWorkflowSchedules ? 200 : 403}>
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionMaintenanceWorkflowSchedulesOverview />

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              {...register('display_name')}
              className="flex-1"
              maxLength={32}
              disabled={updateMaintenanceWorkflowScheduleIsPending}
            />
            <SelectController
              control={control}
              name="kind"
              label="Tipo de manutenção"
              className="flex-1"
              markAsRequired
              options={maintenanceWorkflowSchedulesKindOptions}
              errorMessage={errors.kind?.message || undefined}
              isDisabled={updateMaintenanceWorkflowScheduleIsPending}
            />
          </div>

          <TextArea
            label="Descrição (será exibida na solicitação de manutenção)"
            {...register('observations')}
            markAsRequired
            errorMessage={errors.observations?.message || undefined}
            rows={6}
            disabled={updateMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesEquipments
            control={control}
            errors={errors}
            disabled={updateMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesExecution
            control={control}
            errors={errors}
            disabled={updateMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesObjectives
            control={control}
            errors={errors}
            disabled={updateMaintenanceWorkflowScheduleIsPending}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Update;
